import React, { useEffect, useMemo } from 'react';
import { styled } from '@compiled/react';
import DynamicTable from '@atlaskit/dynamic-table';
import Lozenge from '@atlaskit/lozenge';
import { JsmPageInteractiveEvent } from '@atlassian/jira-servicedesk-common/src/utils/browser-metric/index.tsx';
import type {
	Language,
	TranslationField,
	TranslationPanel,
} from '@atlassian/jira-servicedesk-language-settings-translations-resource/src/services/get-data/types.tsx';
import type { ProjectId, ProjectKey } from '@atlassian/jira-shared-types/src/general.tsx';
import type { ContentPreviewType } from '../../common/types/content-preview';
import type { PreviewSendTestEmailActionsType } from '../../common/types/preview-send-test-email-actions';
import type { SendMeATestEmailType } from '../../common/types/send-me-a-test-email';
import {
	type NotificationVariables,
	useNotificationVariables,
} from '../../services/notification-variables/index.tsx';
import { FlaggableTextField } from './flaggable-textfield';
import NotificationEditor from './notification-editor';

export type Props = {
	translationPanel: TranslationPanel;
	defaultLanguage: Language;
	selectedLanguage: Language;
	projectId: ProjectId;
	projectKey?: ProjectKey;
	ContentPreview?: ContentPreviewType;
	SendMeATestEmailButton?: SendMeATestEmailType;
	PreviewSendTestEmailActions?: PreviewSendTestEmailActionsType;
};

const createEditCell = (
	translationField: TranslationField,
	selectedLanguage: Language,
	isReadOnly: boolean,
	notificationVariables?: NotificationVariables,
) => {
	switch (translationField.inputType) {
		case 'notification-editor':
			return (
				<NotificationEditor
					logicalId={translationField.logicalId}
					isUpToDate={translationField.translationState === 'up_to_date'}
					variableGroups={notificationVariables?.availableVariables || []}
					safeNotificationSettingsEnabled={
						notificationVariables?.safeNotificationSettingsEnabled || false
					}
					content={translationField.translationContentMap[selectedLanguage.key]}
					isReadOnly={isReadOnly}
				/>
			);
		default:
			return (
				<FlaggableTextField
					translationField={translationField}
					selectedLanguage={selectedLanguage}
					isReadOnly={isReadOnly}
				/>
			);
	}
};

const hasMutipleTranslationFields = (translationPanel: TranslationPanel) => {
	const translationFields = translationPanel.translationGroups[0]?.translationFields;
	return (
		translationFields &&
		translationPanel.providerUrlKey === 'notification' &&
		translationFields.length > 1
	);
};

const renderRowContent = (defaultLanguageContent: string) => {
	const content = defaultLanguageContent.split('\n').map((text, index, array) => (
		<React.Fragment key={index}>
			{text}
			{index < array.length - 1 && <br />}
		</React.Fragment>
	));
	return <RowContentWrap>{content}</RowContentWrap>;
};

const createRows = (
	translationPanel: TranslationPanel,
	defaultLanguage: Language,
	selectedLanguage: Language,
	isReadOnly: boolean,
	projectKey?: ProjectKey,
	ContentPreview?: ContentPreviewType,
	SendMeATestEmailButton?: SendMeATestEmailType,
	PreviewSendTestEmailActions?: PreviewSendTestEmailActionsType,
	notificationVariables?: NotificationVariables,
) =>
	translationPanel.translationGroups.flatMap((translationGroup, groupIndex) => {
		const rows = translationGroup.translationFields.map((field, index) => {
			let actionsCell = {};

			const hasNotificationActions = hasMutipleTranslationFields(translationPanel);

			const shouldShowActionsButtons =
				projectKey && ContentPreview && SendMeATestEmailButton && PreviewSendTestEmailActions;

			if (hasNotificationActions && shouldShowActionsButtons) {
				actionsCell = {
					key: `${field.logicalId}-actions`,
					content: (
						<PreviewSendTestEmailActions
							projectKey={projectKey}
							translationPanel={translationPanel}
							defaultLanguage={defaultLanguage}
							selectedLanguage={selectedLanguage}
							ContentPreview={ContentPreview}
							SendMeATestEmail={SendMeATestEmailButton}
							hasMultipleActions
						/>
					),
				};
			}

			const defaultLanguageContent = field.translationContentMap[defaultLanguage.key];

			return {
				key: `row-${index}-${field.logicalId}`,
				isHighlighted: false,
				cells: [
					{
						key: `${field.logicalId}-default-language`,
						content: renderRowContent(defaultLanguageContent),
					},
					{
						key: `${field.logicalId}-selected-language`,
						content: createEditCell(field, selectedLanguage, isReadOnly, notificationVariables),
						colSpan: hasNotificationActions ? 2 : 1,
					},
					...(translationGroup.translationFields.length > 1 &&
					translationPanel.providerUrlKey === 'notification'
						? [actionsCell]
						: []),
				],
			};
		});
		if (translationGroup.displayName) {
			return [
				{
					key: `row-group-${translationGroup.displayName}-${groupIndex}`,
					cells: [
						{
							key: `cell-group-${translationGroup.displayName}-${groupIndex}-default`,
							content: translationGroup.displayName,
						},
						{
							key: `cell-group-${translationGroup.displayName}-${groupIndex}-selected`,
							content: '',
						},
					],
				},
				...rows,
			];
		}
		return rows;
	});

const isNotificationPanel = (translationPanel: TranslationPanel) =>
	translationPanel.translationGroups.some((group) =>
		group.translationFields.some((field) => field.inputType === 'notification-editor'),
	);

export const GenericPanel = ({
	projectId,
	projectKey,
	defaultLanguage,
	selectedLanguage,
	translationPanel,
	ContentPreview,
	SendMeATestEmailButton,
	PreviewSendTestEmailActions,
}: Props) => {
	const { fetch, data: notificationVariableData } = useNotificationVariables(
		projectId,
		translationPanel.entityId || 0,
	);

	const { editDisabled: isReadOnly } = translationPanel;

	useEffect(() => {
		if (translationPanel.entityId && isNotificationPanel(translationPanel)) {
			fetch();
		}
	}, [fetch, translationPanel]);

	const hasNotificationActions = hasMutipleTranslationFields(translationPanel);

	const head = useMemo(
		() => ({
			cells: [
				{
					key: 'defaultLanguage',
					content: (
						<>
							{defaultLanguage.label}&nbsp;&nbsp;
							<Lozenge>Project Default</Lozenge>
						</>
					),
					isSortable: false,
				},
				{
					key: 'selectedLanguage',
					content: selectedLanguage.label,
					isSortable: false,
					colSpan: hasNotificationActions ? 2 : 1,
				},
				...(hasNotificationActions
					? [
							{
								key: 'Actions',
								content: 'Actions',
								isSortable: false,
							},
						]
					: []),
			],
		}),
		[defaultLanguage.label, hasNotificationActions, selectedLanguage.label],
	);

	const rows = useMemo(
		() =>
			createRows(
				translationPanel,
				defaultLanguage,
				selectedLanguage,
				isReadOnly,
				projectKey,
				ContentPreview,
				SendMeATestEmailButton,
				PreviewSendTestEmailActions,
				notificationVariableData,
			),
		[
			projectKey,
			translationPanel,
			defaultLanguage,
			selectedLanguage,
			isReadOnly,
			ContentPreview,
			SendMeATestEmailButton,
			PreviewSendTestEmailActions,
			notificationVariableData,
		],
	);

	return (
		<>
			<JsmPageInteractiveEvent />

			<DynamicTable head={head} rows={rows} isFixedSize />
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RowContentWrap = styled.div({
	top: 0,
});
